<template>
  <div class="space-y-0">
    <!-- Filtros de categoría -->
    <CategoryFilters
      ref="categoryFiltersRef"
      :selected-categories="selectedCategories"
      @update:selected-categories="selectedCategories = $event"
      class="rounded-b-none"
    />

    <!-- Filtros de búsqueda -->
    <SearchFilters
      ref="searchFiltersRef"
      :laboratories="availableLaboratories"
      :delivery-times="deliveryTimeOptions"
      @update:filters="filters = $event"
      class="px-4 py-3 bg-white border-x border-b border-slate-200"
    />

    <!-- Tabla de productos -->
    <div v-if="productStore.loading || categoryStore.loading" class="py-6">
      <Preloader />
    </div>
    
    <template v-else>
      <!-- Resultados y botón de limpiar -->
      <div class="flex justify-between items-center px-4 py-2 bg-white border-x border-b border-slate-200">
        <div class="flex items-center gap-2">
          <p class="text-sm text-slate-600">
            {{ filteredProducts.length }} productos encontrados
          </p>
          <template v-if="selectedCategories.length === 1">
            <span 
              v-if="configStore.getCategoryConfig(selectedCategories[0]).message"
              class="text-sm text-indigo-600 font-medium"
            >
              - {{ configStore.getCategoryConfig(selectedCategories[0]).message }}
            </span>
          </template>
        </div>
        <button 
          v-if="filters.search || filters.lab || filters.deliveryTime"
          @click="clearAllFilters"
          class="text-sm text-slate-600 hover:text-slate-800"
        >
          Limpiar filtros
        </button>
      </div>

      <!-- Tabla de productos -->
      <ProductTable 
        ref="productTableRef"
        :products="filteredProducts"
        :original-products="originalProducts"
        :show-actions="false"
        class="rounded-t-none"
      />

      <!-- Mensaje cuando no hay resultados -->
      <div 
        v-if="filteredProducts.length === 0" 
        class="text-center py-4 text-slate-600 bg-white border-x border-b border-slate-200 rounded-b-lg"
      >
        No se encontraron productos que coincidan con los filtros seleccionados
      </div>
    </template>

    <!-- Add calculator component -->
    <PriceCalculator ref="calculator" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useProductStore, useLaboratoryStore, useCategoryStore, useConfigStore } from '../../stores';
import { processProducts } from '../../utils/productUtils';
import SearchFilters from './SearchFilters.vue';
import ProductTable from './ProductTable.vue';
import CategoryFilters from './CategoryFilters.vue';
import Preloader from '../Preloader.vue';
import PriceCalculator from './PriceCalculator.vue';

// Stores
const productStore = useProductStore();
const laboratoryStore = useLaboratoryStore();
const categoryStore = useCategoryStore();
const configStore = useConfigStore();

// Estado local
const selectedCategories = ref<string[]>([]);
const filters = ref({
  search: '',
  lab: '',
  deliveryTime: ''
});

// Referencias para componentes
const searchFiltersRef = ref();
const categoryFiltersRef = ref();
const calculator = ref();
const productTableRef = ref();

// Cargar datos necesarios
onMounted(async () => {
  await Promise.all([
    categoryStore.fetchCategories(),
    laboratoryStore.fetchLaboratories(),
    productStore.fetchProducts(),
    configStore.fetchConfigurations()
  ]);

  // Seleccionar primera categoría automáticamente
  if (categoryStore.categoryOrder.length > 0) {
    selectedCategories.value = [categoryStore.categoryOrder[0]];
  }

  if (productTableRef.value) {
    productTableRef.value.calculator = calculator.value;
  }
});

// Obtener laboratorios disponibles para los filtros
const availableLaboratories = computed(() => 
  laboratoryStore.laboratories.map(lab => ({
    id: lab.id,
    name: lab.Nombre
  }))
);

// Convert DELIVERY_TIMES to regular array for props
const deliveryTimeOptions = [...productStore.DELIVERY_TIMES];

// Procesar y filtrar productos
const filteredProducts = computed(() => {
  // Primer paso: filtrar por categorías y búsqueda
  const preFilteredProducts = productStore.products.filter(product => {
    // Filtro por categorías
    const matchesCategory = selectedCategories.value.length === 0 || 
      selectedCategories.value.includes(product.Categoria);

    // Filtro por búsqueda
    const matchesSearch = !filters.value.search || 
      product.Nombre.toLowerCase().includes(filters.value.search.toLowerCase());

    // Filtro por laboratorio
    const matchesLab = !filters.value.lab || 
      product.laboratories.some(lab => lab.laboratory.Nombre === filters.value.lab);

    // Filtro por tiempo de entrega
    const matchesDeliveryTime = !filters.value.deliveryTime || 
      product.laboratories.some(lab => lab.details.TiempoDeDemora === filters.value.deliveryTime);

    return matchesCategory && matchesSearch && matchesLab && matchesDeliveryTime;
  });

  // Segundo paso: procesar productos según configuración
  return processProducts(
    preFilteredProducts,
    (categoryId) => configStore.getCategoryConfig(categoryId)
  );
});

// Productos originales (sin procesar) para referencia
const originalProducts = computed(() => {
  return productStore.products.filter(product => {
    const matchesCategory = selectedCategories.value.length === 0 || 
      selectedCategories.value.includes(product.Categoria);
    const matchesSearch = !filters.value.search || 
      product.Nombre.toLowerCase().includes(filters.value.search.toLowerCase());
    return matchesCategory && matchesSearch;
  });
});

// Limpiar todos los filtros
const clearAllFilters = () => {
  selectedCategories.value = [];
  filters.value = {
    search: '',
    lab: '',
    deliveryTime: ''
  };
  if (searchFiltersRef.value) {
    searchFiltersRef.value.resetFilters();
  }
  if (categoryFiltersRef.value) {
    categoryFiltersRef.value.resetFilters();
  }
};
</script>